import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { detectCurrency, formatPrice } from '../../data/pricing';
import { BundlePurchaseDialog } from '../BundlePurchaseDialog';
import { trackBundleView } from '../../utils/analytics';
import {
  Heart, Baby, Briefcase, Dumbbell, PiggyBank,
  Leaf, Plane, PawPrint, GraduationCap, Home,
  ArrowRight, Package, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  heart: Heart,
  baby: Baby,
  briefcase: Briefcase,
  dumbbell: Dumbbell,
  'piggy-bank': PiggyBank,
  leaf: Leaf,
  plane: Plane,
  'paw-print': PawPrint,
  'graduation-cap': GraduationCap,
  home: Home,
  package: Package,
};

const ACCENT_COLORS = [
  { bg: 'bg-rose-50', icon: 'text-rose-400', border: 'border-rose-100' },
  { bg: 'bg-amber-50', icon: 'text-amber-500', border: 'border-amber-100' },
  { bg: 'bg-blue-50', icon: 'text-blue-400', border: 'border-blue-100' },
  { bg: 'bg-emerald-50', icon: 'text-emerald-500', border: 'border-emerald-100' },
  { bg: 'bg-violet-50', icon: 'text-violet-400', border: 'border-violet-100' },
  { bg: 'bg-teal-50', icon: 'text-teal-500', border: 'border-teal-100' },
  { bg: 'bg-sky-50', icon: 'text-sky-400', border: 'border-sky-100' },
  { bg: 'bg-orange-50', icon: 'text-orange-400', border: 'border-orange-100' },
  { bg: 'bg-indigo-50', icon: 'text-indigo-400', border: 'border-indigo-100' },
  { bg: 'bg-stone-100', icon: 'text-stone-500', border: 'border-stone-200' },
];

const BundleCard = ({ bundle, index, currency, onSelect, isOwned }) => {
  const Icon = ICON_MAP[bundle.icon] || Package;
  const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];
  const savings = currency === 'nok' ? bundle.savings_nok : bundle.savings_usd;
  const bundlePrice = currency === 'nok' ? bundle.bundle_price_nok : bundle.bundle_price_usd;
  const totalValue = savings?.individual_total || 0;

  return (
    <div
      data-testid={`bundle-card-${bundle.slug}`}
      onClick={() => onSelect(bundle)}
      className="group relative bg-white rounded-2xl border border-[#d8cfc8]/60 overflow-hidden hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)] hover:-translate-y-1.5 transition-all duration-500 cursor-pointer"
    >
      {/* Header */}
      <div className={`${accent.bg} px-6 pt-6 pb-5 relative`}>
        <div className="flex items-start justify-between">
          <div className={`w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center shadow-sm ${accent.border} border`}>
            <Icon className={`w-5 h-5 ${accent.icon}`} strokeWidth={1.5} />
          </div>
          {savings?.savings_percent > 0 && (
            <span className="text-[10px] font-sans font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/90 text-stone-600 shadow-sm">
              Save {savings.savings_percent}%
            </span>
          )}
        </div>
        <h3 className="font-serif font-bold text-[#111111] text-lg mt-4 leading-tight">{bundle.title}</h3>
        <p className="text-xs font-sans text-[#6F6964] mt-1">{bundle.description}</p>
      </div>

      {/* Products list */}
      <div className="px-6 py-4">
        <div className="space-y-2">
          {bundle.products?.map((product) => (
            <div key={product.slug} className="flex items-center justify-between">
              <span className="text-xs font-sans text-stone-600">{product.title}</span>
              <span className="text-[11px] font-sans text-stone-400 line-through">
                {formatPrice(currency === 'nok' ? product.price_nok : product.price_usd, currency)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Price footer */}
      <div className="px-6 pb-6 pt-2">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wider">Total value</p>
            <p className="text-sm font-sans text-stone-400 line-through">{formatPrice(totalValue, currency)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wider">Collection price</p>
            <p className="text-xl font-serif font-bold text-[#111111]">{formatPrice(bundlePrice, currency)}</p>
          </div>
        </div>
        {isOwned ? (
          <div data-testid={`bundle-owned-${bundle.slug}`} className="w-full text-center py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-sans font-semibold">
            You own this collection
          </div>
        ) : (
          <Button
            data-testid={`bundle-buy-${bundle.slug}`}
            className="w-full bg-[#111111] text-white hover:bg-[#111111]/90 rounded-xl py-5 font-sans font-bold text-sm shadow-md hover:shadow-lg transition-all duration-300"
          >
            Buy collection <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const CollectionsSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated, purchases } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const currency = detectCurrency();

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const res = await axios.get(`${API}/purchases/bundles`);
        setBundles(res.data.bundles || []);
      } catch (err) {
        console.error('Failed to fetch bundles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBundles();
  }, []);

  const isBundleOwned = (bundleSlug) => {
    return purchases.some(p => p.product_slug === bundleSlug && p.purchase_type === 'bundle');
  };

  const handleSelectBundle = (bundle) => {
    trackBundleView(bundle.slug);
    if (isBundleOwned(bundle.slug)) return;
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent('/?collection=' + bundle.slug)}`);
      return;
    }
    setSelectedBundle(bundle);
  };

  if (loading || bundles.length === 0) return null;

  return (
    <>
      <section data-testid="collections-section" className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#111111] text-white text-[11px] font-sans font-bold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Collections
            </div>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#111111] tracking-tight mb-3">
              Save more with collections
            </h2>
            <p className="text-sm font-sans text-[#6F6964] max-w-lg mx-auto">
              Curated product bundles for your lifestyle. Buy together and save up to 31%.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {bundles.slice(0, 8).map((bundle, index) => (
              <BundleCard
                key={bundle.slug}
                bundle={bundle}
                index={index}
                currency={currency}
                onSelect={handleSelectBundle}
                isOwned={isBundleOwned(bundle.slug)}
              />
            ))}
          </div>

          {bundles.length > 8 && (
            <div className="text-center mt-10">
              <Button
                data-testid="see-all-collections-btn"
                variant="outline"
                onClick={() => {
                  const remaining = bundles.slice(8);
                  if (remaining.length) {
                    setBundles(prev => [...prev]);
                  }
                }}
                className="rounded-full px-8 py-5 font-sans font-semibold border-[#d8cfc8] text-[#6F6964] hover:bg-[#F7F5F2] text-sm"
              >
                See all {bundles.length} collections <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}
        </div>
      </section>

      <BundlePurchaseDialog
        bundle={selectedBundle}
        open={!!selectedBundle}
        onOpenChange={(open) => { if (!open) setSelectedBundle(null); }}
        currency={currency}
      />
    </>
  );
};

export { CollectionsSection };
